/**
 * @module Page-Custom-Software-Systems-Integration
 */

import { BANNER_TYPE } from "../../../globals/constants";
import { useEffect } from "react";
import { loadScript } from "../../../globals/common-functions";
import banner from "../../../globals/banner.json";
import AvertImage from "../../../globals/elements/avertimg";
import SectionInnerPageBanner from "../../sections/common/banner";

function CustomSoftwarePage() {

    useEffect(()=>{
        loadScript("js/custom.js");
    })

    return (

        <div className="av-sv2-wrap">
            <SectionInnerPageBanner _data={banner.service_detail} _type={BANNER_TYPE.COMPACT} />

            {/* Hero */}
            <div className="section-full p-t120 p-b0 site-bg-white">
                <div className="container">
                    <div className="av-hero-v2">
                        <div className="row align-items-center">
                            <div className="col-lg-7">
                                <div className="av-hero-eyebrow">
                                    <i className="feather feather-code"></i>
                                    Built for You
                                </div>
                                <h1>Custom Software & Systems Integration</h1>
                                <p className="av-hero-lead">
                                    Off-the-shelf software only gets you so far. When your business has specific needs
                                    that generic tools can't handle, we build something that fits — and we connect
                                    your existing systems so everything works together.
                                </p>
                            </div>
                            <div className="col-lg-5">
                                <div className="av-hero-visual">
                                    <AvertImage src="images/services/custom-software-development.jpg" alt="Custom Software Development" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Two Services */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-layers"></i>
                                </div>
                                <h3>Custom Software Development</h3>
                                <p>We create software that fits how your business actually works — not the other way around. You tell us what you need, and we build it.</p>
                                <ul>
                                    <li>Business process automation</li>
                                    <li>Custom web applications</li>
                                    <li>Database design and development</li>
                                    <li>Workflow management systems</li>
                                    <li>Reporting and analytics dashboards</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-6 m-b30">
                            <div className="av-card-v2">
                                <div className="av-card-icon">
                                    <i className="feather feather-link"></i>
                                </div>
                                <h3>Systems Integration</h3>
                                <p>Tired of copying data between systems or working around tools that don't talk to each other? We connect your software so information flows where it needs to.</p>
                                <ul>
                                    <li>API development and integration</li>
                                    <li>Data synchronisation between platforms</li>
                                    <li>Legacy system modernisation</li>
                                    <li>Third-party service connections</li>
                                    <li>Cloud platform integration</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Process */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-sh">
                        <div className="av-sh-label">How We Build</div>
                        <h2>From Idea to Working Software</h2>
                        <p>We keep things straightforward. You're involved at every step, and you'll never be surprised by what we deliver.</p>
                    </div>

                    <div className="row av-timeline">
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-tl-step">
                                <div className="av-tl-num">1</div>
                                <h4>Understanding Your Needs</h4>
                                <p>We talk through your challenges and goals. No jargon, just clear conversations about what you're trying to achieve.</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-tl-step">
                                <div className="av-tl-num">2</div>
                                <h4>Planning the Solution</h4>
                                <p>We design and prototype so you can see what we're building before a single line of production code is written.</p>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 m-b30">
                            <div className="av-tl-step">
                                <div className="av-tl-num">3</div>
                                <h4>Building & Testing</h4>
                                <p>We develop in stages, testing as we go, and keep you in the loop with regular updates so there are no surprises.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Bar */}
            <div className="av-section-v2 av-bg-slate">
                <div className="container">
                    <div className="av-stats-bar m-b60">
                        <div className="av-sb-item">
                            <div className="av-sb-num">12+</div>
                            <div className="av-sb-label">Years</div>
                        </div>
                        <div className="av-sb-item">
                            <div className="av-sb-num">200+</div>
                            <div className="av-sb-label">Projects</div>
                        </div>
                        <div className="av-sb-item">
                            <div className="av-sb-num">200+</div>
                            <div className="av-sb-label">Clients</div>
                        </div>
                        <div className="av-sb-item">
                            <div className="av-sb-num">10+</div>
                            <div className="av-sb-label">Industries</div>
                        </div>
                    </div>

                    {/* Why Us */}
                    <div className="av-panel-accent">
                        <h3>Why people choose us for custom work</h3>
                        <p>We've built software for businesses of all sizes, and we bring the same care and attention to every project.</p>

                        <div className="row">
                            <div className="col-lg-6">
                                <div className="av-feature-row">
                                    <div className="av-fr-icon"><i className="feather feather-check-circle"></i></div>
                                    <div>
                                        <h5>Built around your workflow</h5>
                                        <p>We don't force you to change how you work. The software adapts to you.</p>
                                    </div>
                                </div>
                                <div className="av-feature-row">
                                    <div className="av-fr-icon"><i className="feather feather-trending-up"></i></div>
                                    <div>
                                        <h5>Designed to grow with you</h5>
                                        <p>Our solutions scale as your business expands, so you won't outgrow them.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="av-feature-row">
                                    <div className="av-fr-icon"><i className="feather feather-smile"></i></div>
                                    <div>
                                        <h5>Easy to use</h5>
                                        <p>We focus on interfaces your team will actually enjoy using, not struggle with.</p>
                                    </div>
                                </div>
                                <div className="av-feature-row">
                                    <div className="av-fr-icon"><i className="feather feather-headphones"></i></div>
                                    <div>
                                        <h5>Ongoing support</h5>
                                        <p>We're here after launch with training, updates, and help whenever you need it.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="av-section-v2">
                <div className="container">
                    <div className="av-cta-v2">
                        <h3>Got a project in mind?</h3>
                        <p>Tell us what you're working with and what you need — we'll figure out the best way to make it happen.</p>
                        <div className="av-cta-actions">
                            <a href="/contact-us" className="av-btn-white">Let's Talk</a>
                            <a href="/success-stories" className="av-btn-ghost">See Our Work</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default CustomSoftwarePage;
